import { analysisCommandSchema, analysisReplySchema, generators, type AnalysisCapabilities, type AnalysisCommand,
  type AnalysisReply, type Lease, type PlaycastMessage, type PlaycastMessageProcessMacroRun } from '@playcastdotio/Messaging';
import { z } from 'zod';
import type { AnalysisClientLogger, AnalysisHostActivity, AnalysisHostSurface, INativeMacroAnalysisClient } from './INativeMacroAnalysisClient';

const terminalLeaseSchema = z.object({
  jobId: z.string().min(1).max(128), manifestDigest: z.string().regex(/^[0-9a-f]{64}$/),
  workPlanDigest: z.string().regex(/^[0-9a-f]{64}$/), hostId: z.string().min(1).max(128),
  socketId: z.string().min(1).max(128), epoch: z.number().int().nonnegative(),
  renewalSequence: z.number().int().nonnegative(), remainingMs: z.number().int().positive().max(20_000),
}).strict();
const terminalIdentitySchema = z.object({
  kind: z.literal('analysis'), requestId: z.string().min(1).max(128), inputsSent: z.literal(false),
  status: z.enum(['failed', 'cancelled', 'completed']), lease: terminalLeaseSchema,
  sequence: z.number().int().nonnegative(),
}).strip();
const leaseIdentityFields = ['jobId', 'manifestDigest', 'workPlanDigest', 'hostId', 'socketId', 'epoch'] as const;

/** Analysis shares the existing IPC envelope but has no playback, capture or input dependency. */
export class NativeMacroAnalysisClient implements INativeMacroAnalysisClient {
  private readonly listeners = new Set<(reply: AnalysisReply) => void>();
  private pending?: { requestId: string; promise: Promise<AnalysisCapabilities>; resolve(value: AnalysisCapabilities): void;
    reject(error: Error): void; timer: ReturnType<typeof setTimeout> };
  private disposed = false;
  private running: AnalysisHostActivity = 'idle';
  private queuedPreview = false;
  private assignment?: { requestId: string; lease: Lease };
  public constructor(private readonly transport: (message: PlaycastMessage<PlaycastMessageProcessMacroRun>) => void,
    private readonly logger: AnalysisClientLogger, private readonly createId = () => crypto.randomUUID()) {}
  public capabilities(): Promise<AnalysisCapabilities> {
    if (this.disposed) return Promise.reject(new Error('analysis_client_disposed'));
    if (this.pending) return this.pending.promise;
    const requestId = this.createId();
    let resolve!: (value: AnalysisCapabilities) => void, reject!: (error: Error) => void;
    const promise = new Promise<AnalysisCapabilities>((accept, fail) => { resolve = accept; reject = fail; });
    const timer = setTimeout(() => this.failCapabilities(requestId, 'analysis_capabilities_timeout'), 5000);
    this.pending = { requestId, promise, resolve, reject, timer };
    void this.send({ command: 'analysisCapabilities', requestId }).catch(() => this.failCapabilities(requestId, 'analysis_native_send_failed'));
    return promise;
  }
  public async send(input: AnalysisCommand): Promise<void> {
    if (this.disposed) throw new Error('analysis_client_disposed');
    const command = analysisCommandSchema.parse(input);
    const previous = this.assignment;
    if (command.command === 'analysisStart' || command.command === 'analysisPreview') {
      this.assignment = { requestId: command.requestId, lease: command.lease };
    }
    try { this.transport(generators.process.macroRun(command)); }
    catch (error) { this.assignment = previous; throw error; }
    if (command.command !== 'analysisRenew' && command.command !== 'analysisStatus') this.log('analysis_native_command', {
      command: command.command, requestId: command.requestId, ...('lease' in command ? { jobId: command.lease.jobId, epoch: command.lease.epoch } : {}) });
  }
  public noteAssignment(running: AnalysisHostActivity, queuedPreview: boolean): void {
    this.running = running;
    this.queuedPreview = queuedPreview;
    this.log('analysis_host_surface', { running, queuedPreview });
  }
  public surface(): AnalysisHostSurface { return { running: this.running, queuedPreview: this.queuedPreview }; }
  public subscribe(listener: (reply: AnalysisReply) => void): () => void {
    if (this.disposed) throw new Error('analysis_client_disposed');
    this.listeners.add(listener); return () => { this.listeners.delete(listener); };
  }
  public notify(input: unknown): boolean {
    if (!input || typeof input !== 'object' || !('kind' in input) || input.kind !== 'analysis') return false;
    if (this.disposed) return true;
    const parsed = analysisReplySchema.safeParse(input);
    const reply = parsed.success ? parsed.data : this.recoverTerminalFailure(input);
    if (!parsed.success) this.log('analysis_native_reply_rejected', { errorCode: 'invalid_analysis_envelope', recovered: !!reply });
    if (!reply) return true;
    if (reply.status === 'capabilities' && this.pending?.requestId === reply.requestId) {
      const pending = this.pending; this.pending = undefined; clearTimeout(pending.timer); pending.resolve(reply.capabilities);
    }
    if (reply.status === 'failed' || reply.status === 'completed' || reply.status === 'cancelled') {
      if (this.matchesAssignment(reply)) this.assignment = undefined;
      this.log('analysis_native_terminal', {
        jobId: reply.lease.jobId, epoch: reply.lease.epoch, status: reply.status, inputsSent: false,
        errorCode: reply.errorCode && /^[a-z_]{1,128}$/.test(reply.errorCode) ? reply.errorCode : '' });
    }
    for (const listener of this.listeners) {
      try { listener(reply); } catch { this.log('analysis_native_listener_failed', { errorCode: 'listener_failed' }); }
    }
    return true;
  }
  public dispose(): void {
    this.disposed = true; this.listeners.clear(); this.assignment = undefined;
    if (this.pending) this.failCapabilities(this.pending.requestId, 'analysis_client_disposed');
  }
  /** An incompatible terminal payload must fail its owned job, never become trusted evidence. */
  private recoverTerminalFailure(input: object): AnalysisReply | undefined {
    const identity = terminalIdentitySchema.safeParse(input);
    if (!identity.success || !Number.isSafeInteger(identity.data.sequence) || !this.matchesAssignment(identity.data)) return undefined;
    return { ...identity.data, status: 'failed', errorCode: 'analysis_native_reply_invalid' };
  }
  private matchesAssignment(reply: { requestId: string; lease: Lease }): boolean {
    const assignment = this.assignment;
    return !!assignment && reply.requestId === assignment.requestId
      && leaseIdentityFields.every(field => reply.lease[field] === assignment.lease[field]);
  }
  private failCapabilities(requestId: string, errorCode: string): void {
    const pending = this.pending;
    if (!pending || pending.requestId !== requestId) return;
    this.pending = undefined; clearTimeout(pending.timer); pending.reject(new Error(errorCode));
    this.log('analysis_native_capabilities_failed', { requestId, errorCode });
  }
  private log(name: string, fields: Record<string, string | number | boolean>): void {
    try { this.logger.event(name, fields); } catch { /* Logging cannot interrupt lease or IPC handling. */ }
  }
}
